import type { Layout } from '@mentimeter/http-clients';
import type { LineHeightsEnum } from '@mentimeter/ragnar-dsc';
import { useRagnar } from '@mentimeter/ragnar-react';
import type { Responsive } from '@mentimeter/ragnar-styled';
import type { BoxT } from '@mentimeter/ragnar-ui/box';
import { Box } from '@mentimeter/ragnar-ui/box';
import { MarkdownText } from '@mentimeter/slides-presentation-ui/text/markdown-text';
import { ResizableTitle } from '@mentimeter/slides-presentation-ui/text/resizable-title';
import { DEFAULT_FONT_OFFSET_TO_TITLE_HEIGHT } from '@mentimeter/slides-presentation-ui/text/resizable-title/font-offset-to-title-size';
import { Description } from '@mentimeter/slides-presentation-ui/title/description';
import { Label } from '@mentimeter/slides-presentation-ui/title/label';
import { TextMarkupRenderer } from '@mentimeter/tiptap/text-markup-renderer';
import { isEmptyObject } from '@tiptap/core';
import { type PropsWithChildren, useState } from 'react';
import { useTranslations } from 'next-intl';
import { usePresentationContext } from '../../../legacy-context/presentationContext';

interface TitleSectionProps extends BoxT {
  title: string;
  styledTitle?: Record<string, unknown>;
  description?: string;
  hideAIEditingSuggestions?: boolean | undefined;
  label?: string;
  placeholder?: string;
  showTextSizeDropdown?: boolean;
  enablePlaceholderAlignment?: boolean | undefined;
  fontSizeOffset: -2 | -1 | 0 | 1 | 2 | undefined;
  textAlign?: 'left' | 'right' | 'center';
  lineHeight?: Responsive<LineHeightsEnum>;
  staticContentProperty?: string;
  fontOffsetToTitleSize?: Record<Layout['font_size_offset'], number>;
}

export function TitleSection({
  title,
  styledTitle,
  description: questionDescription,
  hideAIEditingSuggestions,
  showTextSizeDropdown = false,
  label: titleMeta,
  fontSizeOffset,
  placeholder: propsPlaceholder,
  enablePlaceholderAlignment = false,
  textAlign = 'left',
  lineHeight = 'heading',
  fontOffsetToTitleSize = DEFAULT_FONT_OFFSET_TO_TITLE_HEIGHT,
  children,
  staticContentProperty,
  alignItems = 'flex-start',
  ...props
}: PropsWithChildren<TitleSectionProps>) {
  const t = useTranslations('creation.canvas');
  const placeholder = propsPlaceholder || t('ask_your_question_placeholder');
  const [shouldShowDescription, setShouldShowDescription] = useState(false);

  const { useConfig, InteractiveCanvasComponents } = usePresentationContext();

  const {
    theme: {
      visualizationColors: { titleColor },
    },
  } = useRagnar();

  const { enableAnimations } = useConfig();

  const ConditionalWrapper = InteractiveCanvasComponents?.CanvasTitleArea
    ? InteractiveCanvasComponents?.CanvasTitleArea
    : NonInteractiveWrapper;

  const titleMinHeight = fontOffsetToTitleSize[fontSizeOffset ?? 0];

  return (
    <ConditionalWrapper
      width="100%"
      mb="space14"
      extend={() => ({
        minHeight: `${titleMinHeight}px`,
        '& .hover': {
          opacity: 0,
        },
        '&:hover .hover': {
          opacity: 1,
        },
      })}
      position="relative"
      onMouseEnter={() => setShouldShowDescription(true)}
      onMouseLeave={() => setShouldShowDescription(false)}
      {...props}
    >
      {({ isDisabled, toolbarAnchorRef }) => (
        <Box width="100%" alignItems={alignItems} py="space1" pl="space2">
          {titleMeta && (
            <Label mb="space4">
              <MarkdownText
                disableLineBreaks
                source={titleMeta}
                allowHyperlinks
              />
            </Label>
          )}
          <ResizableTitle
            shouldAnimate={enableAnimations}
            // this value is used only to prevent resizing of an empty string
            // fallback to the placeholder so that the placeholder text is also resized
            titleText={title || placeholder}
            height={titleMinHeight}
            lineHeight={lineHeight}
            textAlign={textAlign}
            color={titleColor}
            nonInteractiveTitleComponent={
              styledTitle && (!isEmptyObject(styledTitle) || !title) ? (
                <TextMarkupRenderer
                  content={styledTitle}
                  placeholder={
                    InteractiveCanvasComponents?.Title ? placeholder : undefined
                  }
                />
              ) : (
                <MarkdownText
                  disableLineBreaks
                  allowHyperlinks
                  source={title}
                />
              )
            }
            interactiveTitleComponent={
              InteractiveCanvasComponents?.Title
                ? InteractiveCanvasComponents.Title({
                    isDisabled,
                    formattedValue: styledTitle,
                    hideAIEditingSuggestions,
                    placeholder,
                    plainValue: title,
                    enablePlaceholderAlignment,
                    staticContentProperty,
                    showTextSizeDropdown,
                    toolbarAnchorRef,
                  })
                : undefined
            }
          />
          {children}
          {questionDescription && (
            <Description
              shouldShow={shouldShowDescription}
              textAlign={textAlign}
            >
              <MarkdownText
                disableLineBreaks={false}
                source={questionDescription}
                allowHyperlinks
              />
            </Description>
          )}
        </Box>
      )}
    </ConditionalWrapper>
  );
}

type NonInteractiveWrapperProps = Omit<BoxT, 'children'> & {
  children: ({
    isDisabled,
    toolbarAnchorRef,
  }: {
    isDisabled: boolean;
    toolbarAnchorRef: undefined;
  }) => React.ReactNode;
};
const NonInteractiveWrapper = ({
  children,
  ...props
}: NonInteractiveWrapperProps) => (
  <Box p="space4" {...props}>
    {children({ isDisabled: false, toolbarAnchorRef: undefined })}
  </Box>
);
